<template>
    <div>
        <vx-tabs v-model="tab" size="lg" role="tablist" aria-label="Not accepted or accepted" class="mb-6">
            <vx-tab
                value="not_accepted"
                :id="notAcceptedTabId"
                :aria-controls="notAcceptedPanelId"
                data-test="not-accepted-tab"
            >
                Not Accepted
            </vx-tab>
            <vx-tab value="accepted" :id="acceptedTabId" :aria-controls="acceptedPanelId" data-test="accepted-tab">
                Accepted
            </vx-tab>
        </vx-tabs>

        <div
            v-show="tab === 'accepted'"
            :id="acceptedPanelId"
            role="tabpanel"
            :aria-labelledby="acceptedTabId"
            data-test="accepted-panel"
        >
            <div class="mb-4 max-w-md">
                <vx-label>Search</vx-label>
                <vx-input
                    v-model="acceptedQuery"
                    type="text"
                    placeholder="External ID, group name or owner email"
                    maxlength="80"
                    size="lg"
                    data-test="accepted-search"
                >
                    <template v-slot:right>
                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                    </template>
                </vx-input>
            </div>

            <page-loading v-if="acceptedLoading && !acceptedList.length" />

            <vx-box
                v-else-if="!acceptedList.length"
                color="info"
                class="px-6 py-12 text-center text-slate-500"
                data-test="accepted-empty"
            >
                {{
                    acceptedQuery.trim()
                        ? 'No acceptances match your search.'
                        : 'No groups have accepted these Terms yet.'
                }}
            </vx-box>

            <template v-else>
                <vx-table class="lg:grid-cols-4-auto" rowBreakpoint="lg">
                    <vx-table-row isHeader>
                        <div>Group</div>
                        <div>Accepted By</div>
                        <div>Accepted At</div>
                    </vx-table-row>

                    <vx-table-row v-for="acceptance in acceptedList" :key="acceptance.id" data-test="accepted-row">
                        <vx-table-row-title>
                            <span class="text-xs sm:text-sm wrap-break-word max-w-[200px]">
                                {{ acceptance.group_name || acceptance.group_id }}
                            </span>
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label>Accepted By:</template>
                            <div class="flex flex-col">
                                <span v-if="acceptance.user?.name">{{ acceptance.user.name }}</span>
                                <span class="text-xs text-slate-500 wrap-break-word">{{ acceptance.user_email }}</span>
                            </div>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label>Accepted At:</template>
                            {{ shortDateTime(acceptance.accepted_at) }}
                        </vx-table-row-item>

                        <vx-table-row-buttons>
                            <vx-button
                                :href="groupHref(acceptance.group_id)"
                                target="_blank"
                                color="muted"
                                outline
                                :data-test="`accepted-view-${acceptance.group_id}`"
                            >
                                View <font-awesome-icon widthAuto :icon="faArrowUpRightFromSquare" />
                            </vx-button>
                        </vx-table-row-buttons>
                    </vx-table-row>
                </vx-table>

                <div v-if="acceptedList.length && !acceptedIsLast" class="mt-4">
                    <vx-button
                        @click="loadAccepted()"
                        color="muted-dark"
                        block
                        :loading="acceptedLoading"
                        data-test="accepted-load-more"
                    >
                        Load More <font-awesome-icon widthAuto :icon="faSpinner" />
                    </vx-button>
                </div>
            </template>
        </div>

        <div
            v-show="tab === 'not_accepted'"
            :id="notAcceptedPanelId"
            role="tabpanel"
            :aria-labelledby="notAcceptedTabId"
            data-test="not-accepted-panel"
        >
            <div class="flex items-end gap-2 mb-4">
                <div class="grow">
                    <vx-label>Search</vx-label>
                    <vx-input
                        v-model="notAcceptedQuery"
                        type="text"
                        placeholder="External ID, group name or owner email"
                        maxlength="80"
                        size="lg"
                        data-test="not-accepted-search"
                    >
                        <template v-slot:right>
                            <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                        </template>
                    </vx-input>
                </div>

                <div class="relative w-50">
                    <vx-label>Group Status</vx-label>
                    <vx-select
                        v-model="notAcceptedStatus"
                        data-test="not-accepted-status-filter"
                        :options="statusFilterOptions"
                    ></vx-select>
                </div>
            </div>

            <page-loading v-if="notAcceptedLoading && !notAcceptedList.length" />

            <vx-box
                v-else-if="!notAcceptedList.length"
                color="info"
                class="px-6 py-12 text-center text-slate-500"
                data-test="not-accepted-empty"
            >
                {{ notAcceptedQuery.trim() ? 'No pending groups match your search.' : 'No groups pending acceptance.' }}
            </vx-box>

            <template v-else>
                <vx-table class="lg:grid-cols-4-auto" rowBreakpoint="lg">
                    <vx-table-row isHeader>
                        <div>Group</div>
                        <div>Owner</div>
                        <div>Status</div>
                        <div class="text-end">
                            <vx-button
                                @click="exportCsv()"
                                type="button"
                                color="muted-dark"
                                size="xs"
                                :loading="exporting"
                                data-test="not-accepted-export"
                            >
                                Export CSV <font-awesome-icon widthAuto :icon="faFileCsv" />
                            </vx-button>
                        </div>
                    </vx-table-row>

                    <vx-table-row v-for="group in notAcceptedList" :key="group.group_id" data-test="not-accepted-row">
                        <vx-table-row-title>
                            <span class="text-xs sm:text-sm wrap-break-word max-w-[200px]">
                                {{ group.name || group.group_id }}
                            </span>
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label>Owner:</template>
                            <div v-if="group.owner" class="flex flex-col">
                                <span>{{ group.owner.name }}</span>
                                <span class="text-xs text-slate-500 wrap-break-word">{{ group.owner.email }}</span>
                            </div>
                            <template v-else>No owner</template>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label>Status:</template>
                            <group-status-badge :status="group.status" />
                        </vx-table-row-item>

                        <vx-table-row-buttons>
                            <vx-button
                                :href="groupHref(group.group_id)"
                                target="_blank"
                                color="muted"
                                outline
                                :data-test="`not-accepted-view-${group.group_id}`"
                            >
                                View <font-awesome-icon widthAuto :icon="faArrowUpRightFromSquare" />
                            </vx-button>
                        </vx-table-row-buttons>
                    </vx-table-row>
                </vx-table>

                <div v-if="notAcceptedList.length && !notAcceptedIsLast" class="mt-4">
                    <vx-button
                        @click="loadNotAccepted()"
                        color="muted-dark"
                        block
                        :loading="notAcceptedLoading"
                        data-test="not-accepted-load-more"
                    >
                        Load More <font-awesome-icon widthAuto :icon="faSpinner" />
                    </vx-button>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    VxBox,
    VxButton,
    VxInput,
    VxLabel,
    VxSelect,
    VxTab,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
    VxTabs,
} from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowUpRightFromSquare, faFileCsv, faMagnifyingGlass, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import type { AxiosResponse } from 'axios';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';
import { shortDateTime } from '~/utils/date';
import { download, prepareCsv } from '~/utils/files';
import { adminTermsClient } from '~/services';
import type { CursorPaginationResponse } from '~/services/api';
import type { CursorPaginationParams } from '~/services/BaseClient.types';
import type {
    PendingAcceptanceGroup,
    PendingGroupsParams,
    Term,
    TermAcceptance,
} from '~/services/adminTermsClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useToasted } from '~/composables/useToasted';
import parseLinkHeader from '~/utils/parseLinkHeader';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';
import {
    GROUP_STATUS_ACTIVE,
    GROUP_STATUS_CLOSED,
    GROUP_STATUS_DRAFT,
    GROUP_STATUS_SUSPENDED,
    type GroupStatus,
} from '~/constants/groupStatuses';

const PER_PAGE = 20;
const EXPORT_PER_PAGE = 100;
const acceptedListCancelKey = Symbol('term-acceptances-accepted');
const notAcceptedListCancelKey = Symbol('term-acceptances-not-accepted');

const props = defineProps<{
    teamId: number;
    term: Term;
}>();

const toasted = useToasted();

const tab = ref<'accepted' | 'not_accepted'>('not_accepted');
const acceptedFetched = ref(false);
const acceptedQuery = ref('');
const notAcceptedQuery = ref('');
const notAcceptedStatus = ref<GroupStatus | ''>('');
const exporting = ref(false);

const notAcceptedTabId = useId();
const acceptedTabId = useId();
const notAcceptedPanelId = useId();
const acceptedPanelId = useId();

const statusFilterOptions = [
    { value: '', label: 'All statuses' },
    { value: GROUP_STATUS_DRAFT, label: 'Draft' },
    { value: GROUP_STATUS_ACTIVE, label: 'Active' },
    { value: GROUP_STATUS_SUSPENDED, label: 'Suspended' },
    { value: GROUP_STATUS_CLOSED, label: 'Closed' },
];

const groupHref = (groupId: string) => `/kiosk/teams/${props.teamId}/groups/${groupId}`;

const {
    list: acceptedList,
    loading: acceptedLoading,
    fetchParams: acceptedParams,
    fetchRecords: fetchAccepted,
    isLast: acceptedIsLast,
} = useCursorPagination<TermAcceptance>();

const {
    list: notAcceptedList,
    loading: notAcceptedLoading,
    fetchParams: notAcceptedParams,
    fetchRecords: fetchNotAccepted,
    isLast: notAcceptedIsLast,
} = useCursorPagination<PendingAcceptanceGroup, PendingGroupsParams>();

const loadAccepted = () => {
    return fetchAccepted(
        adminTermsClient.getTermAcceptances(props.teamId, props.term.id, acceptedParams.value, {
            cancelKey: acceptedListCancelKey,
        })
    );
};

const loadNotAccepted = () => {
    return fetchNotAccepted(
        adminTermsClient.getPendingAcceptanceGroups(props.teamId, props.term.id, notAcceptedParams.value, {
            cancelKey: notAcceptedListCancelKey,
        })
    );
};

const queryParam = (value: string): { query?: string } => {
    const query = value.trim();
    return query ? { query } : {};
};

const acceptedFilters = (): CursorPaginationParams => ({
    per_page: PER_PAGE,
    cursor: null,
    ...queryParam(acceptedQuery.value),
});

const notAcceptedFilters = (): PendingGroupsParams => ({
    per_page: PER_PAGE,
    cursor: null,
    ...queryParam(notAcceptedQuery.value),
    ...(notAcceptedStatus.value ? { status: [notAcceptedStatus.value] } : {}),
});

const reloadAccepted = () => {
    acceptedParams.value = acceptedFilters();
    loadAccepted();
};

const reloadNotAccepted = () => {
    notAcceptedParams.value = notAcceptedFilters();
    loadNotAccepted();
};

const searchAccepted = debounce(reloadAccepted);
const searchNotAccepted = debounce(reloadNotAccepted);

const nextCursorFrom = (response: AxiosResponse<CursorPaginationResponse<PendingAcceptanceGroup>>): string | null => {
    if (response.headers?.link) {
        return parseLinkHeader(response.headers.link)?.next?.cursor ?? null;
    }

    const nextPageUrl = response.data.next_page_url;

    return nextPageUrl ? new URLSearchParams(nextPageUrl.split('?')[1]).get('cursor') : null;
};

const fetchAllNotAccepted = async (): Promise<PendingAcceptanceGroup[]> => {
    const groups: PendingAcceptanceGroup[] = [];
    const filters = notAcceptedFilters();
    let cursor: string | null = null;

    do {
        const response = await adminTermsClient.getPendingAcceptanceGroups(props.teamId, props.term.id, {
            ...filters,
            per_page: EXPORT_PER_PAGE,
            cursor,
        });

        groups.push(...response.data.data);
        cursor = nextCursorFrom(response);
    } while (cursor);

    return groups;
};

const exportCsv = async () => {
    if (exporting.value) {
        return;
    }

    exporting.value = true;

    try {
        const groups = await fetchAllNotAccepted();

        const rows = groups.map((group) => [
            group.group_id,
            group.name,
            group.status,
            group.owner?.name ?? '',
            group.owner?.email ?? '',
        ]);

        download(
            prepareCsv(['group_id', 'name', 'status', 'owner_name', 'owner_email'], rows),
            `team-${props.teamId}-terms-${props.term.id}-not-accepted.csv`,
            { type: 'text/csv;charset=utf-8;' }
        );
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        exporting.value = false;
    }
};

watch(tab, (value) => {
    if (value === 'accepted' && !acceptedFetched.value) {
        acceptedFetched.value = true;
        reloadAccepted();
    }
});

watch(acceptedQuery, () => {
    searchAccepted();
});

watch(notAcceptedQuery, () => {
    searchNotAccepted();
});

watch(notAcceptedStatus, () => {
    searchNotAccepted.cancel();
    reloadNotAccepted();
});

onMounted(reloadNotAccepted);

onBeforeUnmount(() => {
    searchAccepted.cancel();
    searchNotAccepted.cancel();
});
</script>
